import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import cytoscape, { Core, EventObject, NodeSingular, EdgeSingular, NodeDataDefinition, EdgeDataDefinition } from 'cytoscape';
import '../../core/cytoscape-setup';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { PendingReviewsBanner } from '../../shared/components/pending-reviews-banner/pending-reviews-banner';
import { Toast } from '../../shared/services/toast';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { KnowledgeGraph, KnowledgeGraphDocumentItem, KnowledgeGraphNode, KnowledgeGraphEdge } from '../../core/services/knowledge-graph';
import { GraphPlayground, PlaygroundGraph } from '../../core/services/graph-playground';

type LayoutName = 'fcose' | 'breadthfirst' | 'grid' | 'circle' | 'concentric';
type AskStatus = 'idle' | 'searching' | 'answering' | 'done';

interface TraceStep {
  depth: number;
  from: string;
  relationship: string;
  to: string;
}

// How long the on-canvas search animation may take in total, however many nodes the traversal visits.
const ASK_STEP_MS = 140;
const ASK_MAX_ANIMATION_MS = 4500;

const NODE_BATCH_SIZE = 300;
const MAX_LABEL_LENGTH = 22;

// Combined (all-documents) view only -- the auto-load chain stops here rather
// than streaming in the whole graph, since a heavily-ingested tenant's graph
// can run into the tens of thousands of nodes/edges. Backend now orders
// combined-scope nodes by degree, so this cap is the most-connected subset,
// not an arbitrary alphabetical slice. loadMore() resumes the same chain for
// another increment of this size.
const DEFAULT_NODE_CAP = 500;

// Also stops the auto-load chain independent of the node cap. Degree-sorted
// "most connected" nodes are dense by construction -- capping only node
// count doesn't cap edge count, and edge count (not node count) is what
// actually drives fcose's layout cost. Observed live: 600 nodes pulled in
// 15,169 edges (~25/node average) and hung the tab on a single layout pass.
const MAX_AUTO_EDGES = 3000;

function truncateLabel(name: string, maxLength = MAX_LABEL_LENGTH): string {
  if (!name || name.length <= maxLength) return name;
  return `${name.slice(0, maxLength - 1)}…`;
}

interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  targetId: string | null;
}

@Component({
  selector: 'app-knowledge-graph',
  standalone: true,
  imports: [CommonModule, FormsModule, Sidebar, Navbar, ToastComponent, EmptyState, PendingReviewsBanner],
  templateUrl: './knowledge-graph.html',
  styleUrl: './knowledge-graph.css'
})
export class KnowledgeGraphPage implements OnInit, AfterViewInit, OnDestroy {
  private knowledgeGraphService = inject(KnowledgeGraph);
  private toast = inject(Toast);
  // The same search the Graph Playground runs (seed nodes, breadth-first walk, facts for the answer), pointed at the nodes
  // and edges this page has loaded instead of the bundled demo dataset.
  private graphSearch = inject(GraphPlayground);

  @ViewChild('graphCanvas') graphCanvas?: ElementRef<HTMLDivElement>;

  private cy: Core | null = null;
  private askTimers: ReturnType<typeof setTimeout>[] = [];
  private askRequestToken = 0;
  // Where the nodes a search used were before they were lifted out, so they can be put back.
  private liftedPositions = new Map<string, { x: number; y: number }>();

  // Incremental loading state — nodes stream in NODE_BATCH_SIZE batches so
  // the UI never blocks on a huge one-shot fetch+layout. Edges are fetched
  // once and "realized" into the graph as their endpoint nodes arrive.
  private nodeOffset = 0;
  private allEdges: KnowledgeGraphEdge[] = [];
  // Edges touching each node id, built once per load from allEdges -- lets
  // appendBatch() find newly-drawable edges for just the nodes a batch
  // actually added, instead of rescanning every fetched edge on every batch.
  private edgesByNode = new Map<string, KnowledgeGraphEdge[]>();
  private loadedNodeIds = new Set<string>();
  private addedEdgeIds = new Set<string>();
  private loadRequestToken = 0;
  private lastHasMoreNodes = false;
  // Combined-scope only: the chain auto-stops once loadedNodeIds reaches this
  // many, rather than streaming until hasMoreNodes is false. loadMore() bumps
  // it by another DEFAULT_NODE_CAP and resumes the chain.
  private capTarget = DEFAULT_NODE_CAP;

  documents = signal<KnowledgeGraphDocumentItem[]>([]);
  selectedDocument = signal('');

  isLoadingDocuments = signal(true);
  isLoadingGraph = signal(true);
  isStreamingMore = signal(false);
  isLoadingMore = signal(false);

  nodeCount = signal(0);
  edgeCount = signal(0);
  totalNodeCount = signal<number | null>(null);
  canLoadMore = signal(false);

  scope = signal<'combined' | 'document'>('combined');

  selectedType = signal<'node' | 'edge' | null>(null);
  selectedTitle = signal('');
  selectedSubtitle = signal('');
  selectedMeta = signal<Array<{ label: string; value: string }>>([]);

  currentLayout = signal<LayoutName>('fcose');

  contextMenu = signal<ContextMenuState>({ visible: false, x: 0, y: 0, targetId: null });

  // Ask the graph
  question = signal('');
  askStatus = signal<AskStatus>('idle');
  askAnswer = signal('');
  askIsLocalPreview = signal(false);
  askSeeds = signal<string[]>([]);
  askTrace = signal<TraceStep[]>([]);
  askNodeTotal = signal(0);
  askEdgeTotal = signal(0);
  askOpen = signal(true);
  askLifted = signal(false);
  readonly askBusy = computed(() => this.askStatus() === 'searching' || this.askStatus() === 'answering');

  readonly availableLayouts: LayoutName[] = ['fcose', 'breadthfirst', 'grid', 'circle', 'concentric'];

  // Color palette for nodes
  private readonly colors = {
    entity: { bg: '#DBEAFE', border: '#2563EB', highlightBg: '#BFDBFE', highlightBorder: '#1D4ED8' },
    document: { bg: '#EDE9FE', border: '#7C3AED', highlightBg: '#DDD6FE', highlightBorder: '#6D28D9' },
    edge: { color: '#94A3B8', highlight: '#0EA5E9' }
  };

  ngOnInit(): void {
    this.loadDocuments();
    this.loadGraph();
  }

  ngAfterViewInit(): void {
    this.initGraph();
  }

  ngOnDestroy(): void {
    this.askRequestToken++;
    this.stopAskAnimation();
    this.cy?.destroy();
    this.cy = null;
  }

  @HostListener('document:click')
  onDocumentClick() {
    if (this.contextMenu().visible) {
      this.contextMenu.set({ visible: false, x: 0, y: 0, targetId: null });
    }
  }

  loadDocuments() {
    this.isLoadingDocuments.set(true);
    this.knowledgeGraphService.getDocuments().subscribe({
      next: (res) => {
        this.isLoadingDocuments.set(false);
        this.documents.set(res.documents || []);
      },
      error: (err) => {
        this.isLoadingDocuments.set(false);
        const message = err?.error?.message || 'Failed to load graph documents.';
        this.toast.show(message, 'error');
      }
    });
  }

  onDocumentChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedDocument.set(value);
    this.clearSelection();
    this.loadGraph();
  }

  loadGraph() {
    const token = ++this.loadRequestToken;

    this.isLoadingGraph.set(true);
    this.isStreamingMore.set(false);
    this.isLoadingMore.set(false);
    this.nodeOffset = 0;
    this.allEdges = [];
    this.edgesByNode.clear();
    this.loadedNodeIds.clear();
    this.addedEdgeIds.clear();
    this.nodeCount.set(0);
    this.edgeCount.set(0);
    this.totalNodeCount.set(null);
    this.canLoadMore.set(false);
    this.lastHasMoreNodes = false;
    this.capTarget = DEFAULT_NODE_CAP;
    this.clearSelection();
    this.clearAsk(false);
    this.cy?.elements().remove();

    this.fetchNextBatch(token, true);
  }

  /** Resumes the same batch chain for another DEFAULT_NODE_CAP worth of nodes. */
  loadMore() {
    if (!this.canLoadMore() || this.isLoadingMore()) return;
    this.canLoadMore.set(false);
    this.isLoadingMore.set(true);
    this.capTarget += DEFAULT_NODE_CAP;
    this.fetchNextBatch(this.loadRequestToken, false);
  }

  private fetchNextBatch(token: number, isFirstBatch: boolean) {
    const documentName = this.selectedDocument().trim();

    this.knowledgeGraphService.getGraph({
      documentName: documentName || undefined,
      // The document itself is implied by having filtered to it — showing
      // its node here would just be a redundant hub every entity connects
      // to, so mentions (which is what pulls the Document node in) stay off
      // for single-document scope.
      includeMentions: false,
      nodeOffset: this.nodeOffset,
      limitNodes: NODE_BATCH_SIZE
    }).subscribe({
      next: (res) => {
        if (token !== this.loadRequestToken) return; // a newer loadGraph() superseded this one

        if (isFirstBatch) {
          this.isLoadingGraph.set(false);
          this.scope.set(res.scope);
          this.allEdges = res.edges || [];
          this.edgesByNode.clear();
          for (const edge of this.allEdges) {
            this.indexEdge(edge.source, edge);
            this.indexEdge(edge.target, edge);
          }
          this.totalNodeCount.set(res.meta?.totalNodeCount ?? null);
        }

        const batchNodes = res.nodes || [];
        this.nodeOffset += batchNodes.length;
        this.appendBatch(batchNodes, isFirstBatch);

        const hasMore = !!res.meta?.hasMoreNodes;
        this.lastHasMoreNodes = hasMore;
        this.isStreamingMore.set(hasMore);

        // Combined scope stops at capTarget rather than streaming until
        // hasMore is false -- document scope has no cap (totalNodeCount is
        // only populated for combined, per the backend), so it keeps its
        // original streamed-to-completion behavior.
        const cappedScope = res.scope === 'combined';
        const reachedNodeCap = cappedScope && this.loadedNodeIds.size >= this.capTarget;
        const reachedEdgeCap = cappedScope && this.addedEdgeIds.size >= MAX_AUTO_EDGES;
        const reachedCap = reachedNodeCap || reachedEdgeCap;

        if (hasMore && !reachedCap) {
          this.fetchNextBatch(token, false);
          return;
        }

        // Chain finished (either genuinely no more nodes, or the cap was
        // reached) -- this is the one point a full relayout of everything
        // added since the first batch actually runs, instead of once per
        // 300-node batch.
        this.isLoadingMore.set(false);
        this.canLoadMore.set(cappedScope && hasMore);
        if (!isFirstBatch) {
          requestAnimationFrame(() => this.runFcoseLayout(true));
        }
      },
      error: (err) => {
        if (token !== this.loadRequestToken) return;
        this.isStreamingMore.set(false);
        this.isLoadingMore.set(false);
        if (isFirstBatch) {
          this.isLoadingGraph.set(false);
          const message = err?.error?.message || 'Failed to load knowledge graph.';
          this.toast.show(message, 'error');
        } else {
          this.toast.show('Stopped loading more of the graph after a network error.', 'error');
        }
      }
    });
  }

  // ---------------------------------------------------------------- Ask the graph

  /** The nodes and edges currently drawn, in the shape the playground's search expects. */
  private loadedGraph(): PlaygroundGraph {
    const cy = this.cy;
    return {
      documentName: this.selectedDocument(),
      nodes: cy ? cy.nodes().map(n => ({
        id: n.id(),
        name: (n.data('fullName') as string) || (n.data('name') as string) || n.id(),
        type: (n.data('type') as string) || ''
      })) : [],
      edges: cy ? cy.edges().map(e => ({
        id: e.id(),
        source: e.data('source') as string,
        target: e.data('target') as string,
        relationship: (e.data('relationship') as string) || (e.data('type') as string) || ''
      })) : []
    };
  }

  askGraph() {
    const question = this.question().trim();
    if (!question || this.askBusy() || !this.cy) return;

    const graph = this.loadedGraph();
    const token = ++this.askRequestToken;
    this.stopAskAnimation();
    this.releaseFocus(false);
    this.cy.elements().unselect();
    this.clearSelection();
    this.askAnswer.set('');
    this.askIsLocalPreview.set(false);
    this.askTrace.set([]);
    this.askStatus.set('searching');

    // Entities named in the question come first, then looser word matches; a handful of seeds keeps the walk readable.
    const q = question.toLowerCase();
    const nodeById = new Map(graph.nodes.map(n => [n.id, n]));
    const seedIds = this.graphSearch.findSeedNodes(graph, question)
      .sort((a, b) => {
        const rank = (id: string) => {
          const n = nodeById.get(id)!;
          return (q.includes(n.name.toLowerCase()) ? 0 : 2) + (n.type === 'Document' ? 1 : 0);
        };
        return rank(a) - rank(b);
      })
      .slice(0, 6);
    this.askSeeds.set(seedIds.map(id => nodeById.get(id)?.name ?? id));

    if (seedIds.length === 0) {
      this.askAnswer.set(`Nothing on the graph matches that question. Try naming something that is shown here (${graph.nodes.length} nodes loaded).`);
      this.askStatus.set('done');
      this.askNodeTotal.set(0);
      this.askEdgeTotal.set(0);
      return;
    }

    const { order, nodeIds, edgeIds } = this.graphSearch.traverse(graph, seedIds);
    this.askNodeTotal.set(nodeIds.length);
    this.askEdgeTotal.set(edgeIds.length);

    seedIds.forEach(id => this.cy?.getElementById(id).addClass('query-seed'));
    const stepMs = Math.min(ASK_STEP_MS, ASK_MAX_ANIMATION_MS / Math.max(order.length, 1));
    const seedSet = new Set(seedIds);

    order.forEach((step, i) => {
      this.askTimers.push(setTimeout(() => {
        if (token !== this.askRequestToken || !this.cy) return;
        if (!seedSet.has(step.nodeId)) this.cy.getElementById(step.nodeId).addClass('query-visiting');
        if (step.edgeId) {
          const edge = this.cy.getElementById(step.edgeId);
          edge.addClass('query-visiting-edge');
          if (edge.nonempty()) {
            this.askTrace.update(list => [...list, {
              depth: step.depth,
              from: nodeById.get(edge.data('source') as string)?.name ?? '',
              relationship: (edge.data('relationship') as string) || '',
              to: nodeById.get(edge.data('target') as string)?.name ?? ''
            }]);
          }
        }
      }, i * stepMs));
    });

    this.askTimers.push(setTimeout(() => {
      if (token !== this.askRequestToken || !this.cy) return;
      this.setQueryContext(nodeIds, edgeIds, seedIds);
      this.askStatus.set('answering');

      const facts = this.graphSearch.buildFacts(graph, nodeIds, 40);
      this.graphSearch.askBackend(question, facts).subscribe({
        next: (res) => {
          if (token !== this.askRequestToken) return;
          this.askAnswer.set(res.answer || 'The answer service returned an empty response.');
          this.askStatus.set('done');
        },
        error: () => {
          if (token !== this.askRequestToken) return;
          this.askIsLocalPreview.set(true);
          this.askAnswer.set(
            `No answer service is connected right now. Here is what the search found (${nodeIds.length} nodes, ${edgeIds.length} edges):\n\n` +
            facts.slice(0, 10).map(f => `• ${f}`).join('\n')
          );
          this.askStatus.set('done');
        }
      });
    }, order.length * stepMs + 80));
  }

  private setQueryContext(nodeIds: string[], edgeIds: string[], seedIds: string[]) {
    if (!this.cy) return;
    const nodeSet = new Set(nodeIds);
    const edgeSet = new Set(edgeIds);
    const seedSet = new Set(seedIds);

    // Only the nodes and links the search used change colour; the rest of the graph is left exactly as it was.
    this.cy.nodes().forEach(n => {
      n.removeClass('query-visiting query-seed query-context');
      if (seedSet.has(n.id())) n.addClass('query-seed');
      else if (nodeSet.has(n.id())) n.addClass('query-context');
    });
    this.cy.edges().forEach(e => {
      e.removeClass('query-visiting-edge query-context-edge');
      if (edgeSet.has(e.id())) e.addClass('query-context-edge');
    });

    // Once the colours have started to ease in, the nodes that were used glide out to the side of the graph.
    const used = this.cy.nodes().filter(n => nodeSet.has(n.id()) || seedSet.has(n.id()));
    this.askTimers.push(setTimeout(() => this.liftResult(used, seedSet), 350));
  }

  /**
   * Moves the nodes the search used out to the right of the graph, into an arrangement of their own with the starting
   * nodes in the middle, with an eased animation. Everything else stays where it is.
   */
  private liftResult(used: cytoscape.NodeCollection, seedSet: Set<string>) {
    const cy = this.cy;
    if (!cy || used.empty()) return;

    this.liftedPositions.clear();
    used.forEach(n => { this.liftedPositions.set(n.id(), { x: n.position('x'), y: n.position('y') }); });
    this.askLifted.set(true);

    // A square just beyond the right edge of what stays behind, roomy enough for the rings (avoidOverlap grows them if not).
    const rest = cy.nodes().not(used);
    const side = Math.max(500, used.length * 55);
    const anchor = rest.nonempty()
      ? rest.boundingBox()
      : { x1: 0, x2: 0, y1: 0, y2: 0, w: 0, h: 0 };
    const left = anchor.x2 + 160;
    const top = anchor.y1 + (anchor.h - side) / 2;

    const layout = used.union(used.edgesWith(used)).layout({
      name: 'concentric',
      concentric: (n: NodeSingular) => (seedSet.has(n.id()) ? 2 : 1),
      levelWidth: () => 1,
      avoidOverlap: true,
      minNodeSpacing: 40,
      boundingBox: { x1: left, y1: top, w: side, h: side },
      animate: true,
      animationDuration: 1100,
      animationEasing: 'ease-in-out-cubic',
      fit: false
    } as unknown as cytoscape.LayoutOptions);

    // Bring the camera to them once they have landed, leaving some of the graph in view alongside.
    layout.one('layoutstop', () => {
      cy.animate({ fit: { eles: used, padding: 120 }, duration: 700, easing: 'ease-in-out-cubic' });
    });
    layout.run();
  }

  /** Eases the lifted nodes back to where they were, then drops the highlighting. */
  releaseFocus(animate = true) {
    const cy = this.cy;
    const positions = new Map(this.liftedPositions);
    this.liftedPositions.clear();
    this.askLifted.set(false);
    if (!cy) return;

    const finish = () => this.resetQueryStyles();
    if (!animate || positions.size === 0) {
      positions.forEach((pos, id) => cy.getElementById(id).position(pos));
      finish();
      return;
    }

    const token = this.askRequestToken;
    positions.forEach((pos, id) => {
      cy.getElementById(id).animate({ position: pos }, { duration: 900, easing: 'ease-in-out-cubic' });
    });
    cy.animate({ fit: { eles: cy.elements(), padding: 50 }, duration: 900, easing: 'ease-in-out-cubic' });
    this.askTimers.push(setTimeout(() => { if (token === this.askRequestToken) finish(); }, 950));
  }

  clearAsk(animate = true) {
    this.askRequestToken++;
    this.stopAskAnimation();
    this.releaseFocus(animate);
    this.askStatus.set('idle');
    this.askAnswer.set('');
    this.askIsLocalPreview.set(false);
    this.askSeeds.set([]);
    this.askTrace.set([]);
    this.askNodeTotal.set(0);
    this.askEdgeTotal.set(0);
    this.question.set('');
  }

  private resetQueryStyles() {
    this.cy?.nodes().removeClass('query-seed query-context query-visiting');
    this.cy?.edges().removeClass('query-context-edge query-visiting-edge');
  }

  private stopAskAnimation() {
    this.askTimers.forEach(t => clearTimeout(t));
    this.askTimers = [];
  }

  private indexEdge(nodeId: string, edge: KnowledgeGraphEdge) {
    const list = this.edgesByNode.get(nodeId);
    if (list) list.push(edge);
    else this.edgesByNode.set(nodeId, [edge]);
  }

  resetView() {
    this.cy?.fit(undefined, 50);
  }

  applyLayout(layout: LayoutName) {
    this.currentLayout.set(layout);
    if (!this.cy) return;
    this.releaseFocus(false); // a fresh layout replaces the arrangement the search lifted nodes into

    // Cytoscape measures the container's actual pixel size when a layout
    // runs (force-directed layouts need it to compute physics bounds). On
    // first load that size can still be stale/zero if the browser hasn't
    // finished laying out the page yet, silently collapsing every node to
    // one invisible point — resize() forces a fresh measurement immediately
    // before every layout run, not just the first.
    this.cy.resize();

    if (layout === 'fcose') {
      // User-triggered relayout: always a fresh randomize, not incremental.
      this.runFcoseLayout(false);
      this.cy.fit(undefined, 50);
      return;
    }

    const layoutOptions: Record<string, unknown> = {
      name: layout,
      animate: true,
      animationDuration: 500,
      fit: true,
      padding: 40
    };

    if (layout === 'breadthfirst') {
      layoutOptions['directed'] = true;
      layoutOptions['spacingFactor'] = 2.0;
    }

    if (layout === 'grid') {
      layoutOptions['spacingFactor'] = 2.0;
    }

    if (layout === 'circle' || layout === 'concentric') {
      layoutOptions['spacingFactor'] = 1.8;
    }

    this.cy.layout(layoutOptions as unknown as cytoscape.LayoutOptions).run();
  }

  clearSelection() {
    this.selectedType.set(null);
    this.selectedTitle.set('');
    this.selectedSubtitle.set('');
    this.selectedMeta.set([]);
  }

  /** Compute node diameter based on label length. */
  private nodeSize(ele: NodeSingular): number {
    const name = (ele.data('name') as string) || '';
    const base = 72;
    const extra = Math.max(0, name.length - 14) * 3.5;
    return Math.min(base + extra, 180);
  }

  /** Compute text-max-width proportional to the node size. */
  private nodeTextMaxWidth(ele: NodeSingular): string {
    const size = this.nodeSize(ele);
    return `${Math.round(size * 0.85)}px`;
  }

  /** Compute document node diameter based on filename length — document
   * names run much longer than entity names, so they need a larger base
   * and a bigger cap or the wrapped label overflows the fixed-size shape. */
  private documentNodeSize(ele: NodeSingular): number {
    const name = (ele.data('name') as string) || '';
    const base = 82;
    const extra = Math.max(0, name.length - 18) * 3;
    return Math.min(base + extra, 240);
  }

  private documentNodeTextMaxWidth(ele: NodeSingular): string {
    const size = this.documentNodeSize(ele);
    return `${Math.round(size * 0.85)}px`;
  }

  trackByDocumentName(_: number, item: KnowledgeGraphDocumentItem) {
    return item.name;
  }

  // Context menu actions
  onContextExpand() {
    const targetId = this.contextMenu().targetId;
    this.contextMenu.set({ visible: false, x: 0, y: 0, targetId: null });
    if (targetId && this.cy) {
      const node = this.cy.getElementById(targetId);
      if (node && !node.removed()) {
        // Highlight neighbors
        const neighborhood = node.closedNeighborhood();
        this.cy.elements().removeClass('highlighted');
        neighborhood.addClass('highlighted');
        this.cy.fit(neighborhood, 60);
      }
    }
  }

  onContextFocus() {
    const targetId = this.contextMenu().targetId;
    this.contextMenu.set({ visible: false, x: 0, y: 0, targetId: null });
    if (targetId && this.cy) {
      const node = this.cy.getElementById(targetId);
      if (node && !node.removed()) {
        this.cy.animate({ center: { eles: node }, zoom: 2 }, { duration: 400 });
      }
    }
  }

  onContextCopy() {
    const targetId = this.contextMenu().targetId;
    this.contextMenu.set({ visible: false, x: 0, y: 0, targetId: null });
    if (targetId) {
      navigator.clipboard.writeText(targetId).catch(() => {});
      this.toast.show('Node ID copied to clipboard.', 'success');
    }
  }

  private initGraph() {
    const container = this.graphCanvas?.nativeElement;
    if (!container || this.cy) return;

    this.cy = cytoscape({
      container,
      elements: [],
      style: [
        // Colour changes ease over instead of snapping, so the "ask the graph" highlights fade in.
        { selector: 'node', style: { 'transition-property': 'background-color, border-color, border-width, color, opacity', 'transition-duration': '0.5s', 'transition-timing-function': 'ease-in-out' } as never },
        { selector: 'edge', style: { 'transition-property': 'line-color, target-arrow-color, width, opacity', 'transition-duration': '0.5s', 'transition-timing-function': 'ease-in-out' } as never },
        // Entity nodes — size scales with label length to prevent overflow
        {
          selector: 'node[type="Entity"]',
          style: {
            'background-color': this.colors.entity.bg,
            'border-color': this.colors.entity.border,
            'border-width': 2,
            'label': 'data(name)',
            'font-family': 'Poppins, sans-serif',
            'font-size': '11px',
            'color': '#0F172A',
            'text-valign': 'center',
            'text-halign': 'center',
            'text-wrap': 'wrap',
            'text-max-width': (ele: NodeSingular) => this.nodeTextMaxWidth(ele),
            'width': (ele: NodeSingular) => this.nodeSize(ele),
            'height': (ele: NodeSingular) => this.nodeSize(ele),
            'shape': 'ellipse'
          }
        },
        // Document nodes — sized dynamically to fit the filename, same
        // pattern as Entity nodes, since a fixed box overflows on long names
        {
          selector: 'node[type="Document"]',
          style: {
            'background-color': this.colors.document.bg,
            'border-color': this.colors.document.border,
            'border-width': 2,
            'label': 'data(name)',
            'font-family': 'Poppins, sans-serif',
            'font-size': '11px',
            'color': '#0F172A',
            'text-valign': 'center',
            'text-halign': 'center',
            'text-wrap': 'wrap',
            'text-max-width': (ele: NodeSingular) => this.documentNodeTextMaxWidth(ele),
            'width': (ele: NodeSingular) => this.documentNodeSize(ele),
            'height': (ele: NodeSingular) => this.documentNodeSize(ele),
            'shape': 'roundrectangle'
          }
        },
        // Node hover
        {
          selector: 'node:active',
          style: {
            'overlay-opacity': 0.15,
            'overlay-color': '#0F172A'
          }
        },
        // Selected nodes
        {
          selector: 'node:selected',
          style: {
            'border-width': 3,
            'border-color': '#6366F1',
            'overlay-opacity': 0.2,
            'overlay-color': '#6366F1'
          }
        },
        // Highlighted nodes (context menu action)
        {
          selector: 'node.highlighted',
          style: {
            'border-width': 3,
            'border-color': '#F59E0B'
          }
        },
        {
          selector: 'edge.highlighted',
          style: {
            'width': 3,
            'line-color': '#F59E0B'
          }
        },
        // Edges
        {
          selector: 'edge',
          style: {
            'width': 1.5,
            'line-color': this.colors.edge.color,
            'target-arrow-color': this.colors.edge.color,
            'target-arrow-shape': 'triangle',
            'arrow-scale': 0.8,
            'curve-style': 'bezier',
            'label': 'data(relationship)',
            'font-family': 'Poppins, sans-serif',
            'font-size': '10px',
            'color': '#64748B',
            'text-rotation': 'autorotate',
            'text-margin-y': -8
          }
        },
        // Edge hover
        {
          selector: 'edge:active',
          style: {
            'width': 2.5,
            'line-color': this.colors.edge.highlight,
            'target-arrow-color': this.colors.edge.highlight
          }
        },
        // Selected edges
        {
          selector: 'edge:selected',
          style: {
            'width': 2.5,
            'line-color': '#6366F1',
            'target-arrow-color': '#6366F1'
          }
        },
        // "Ask the graph" states. These come last so they win over the type styles above.
        {
          selector: 'node.query-seed',
          style: { 'background-color': '#C7D2FE', 'border-color': '#4F46E5', 'border-width': 5 }
        },
        {
          selector: 'node.query-visiting',
          style: { 'background-color': '#FDE68A', 'border-color': '#F59E0B', 'border-width': 4 }
        },
        {
          selector: 'node.query-context',
          style: { 'background-color': '#BBF7D0', 'border-color': '#16A34A', 'border-width': 3 }
        },
        {
          selector: 'edge.query-visiting-edge',
          style: { 'width': 3, 'line-color': '#F59E0B', 'target-arrow-color': '#F59E0B' }
        },
        {
          selector: 'edge.query-context-edge',
          style: { 'width': 3, 'line-color': '#16A34A', 'target-arrow-color': '#16A34A', 'color': '#15803D' }
        }
      ],
      layout: { name: 'preset' },
      wheelSensitivity: 0.3,
      minZoom: 0.1,
      maxZoom: 4,
      boxSelectionEnabled: true
    });

    // Selection events
    this.cy.on('select', 'node', (evt: EventObject) => {
      const node = evt.target as NodeSingular;
      const data = node.data() as NodeDataDefinition;
      this.showNodeDetails(data);
    });

    this.cy.on('select', 'edge', (evt: EventObject) => {
      const edge = evt.target as EdgeSingular;
      const data = edge.data() as EdgeDataDefinition;
      this.showEdgeDetails(data);
    });

    this.cy.on('unselect', () => {
      this.clearSelection();
    });

    // Right-click context menu on nodes
    this.cy.on('cxttap', 'node', (evt: EventObject) => {
      const node = evt.target as NodeSingular;
      this.contextMenu.set({
        visible: true,
        x: evt.renderedPosition?.x ?? evt.originalEvent.clientX,
        y: evt.renderedPosition?.y ?? evt.originalEvent.clientY,
        targetId: node.id()
      });
    });

    // Right-click on background hides menu
    this.cy.on('cxttap', (evt: EventObject) => {
      if (evt.target === this.cy) {
        this.contextMenu.set({ visible: false, x: 0, y: 0, targetId: null });
      }
    });
  }

  private showNodeDetails(data: NodeDataDefinition) {
    this.selectedType.set('node');
    this.selectedTitle.set((data['fullName'] as string) || (data['name'] as string) || data.id || '');
    this.selectedSubtitle.set((data['type'] as string) || 'Node');

    const meta: Array<{ label: string; value: string }> = [
      { label: 'ID', value: data.id || '' },
      { label: 'Type', value: (data['type'] as string) || '—' }
    ];

    if (data['documentId']) {
      meta.push({ label: 'Document ID', value: data['documentId'] as string });
    }

    if (data['metadata']) {
      try {
        const parsed = JSON.parse(data['metadata'] as string);
        for (const [key, val] of Object.entries(parsed)) {
          if (key === 'embedding' || key === 'embedding_model') continue;
          meta.push({ label: key, value: String(val) });
        }
      } catch {
        meta.push({ label: 'Metadata', value: data['metadata'] as string });
      }
    }

    this.selectedMeta.set(meta);
  }

  private showEdgeDetails(data: EdgeDataDefinition) {
    this.selectedType.set('edge');
    this.selectedTitle.set((data['relationship'] as string) || (data['type'] as string) || 'Relationship');
    this.selectedSubtitle.set('Edge');

    this.selectedMeta.set([
      { label: 'ID', value: data.id || '' },
      { label: 'Type', value: (data['type'] as string) || '—' },
      { label: 'Source', value: data.source || '' },
      { label: 'Target', value: data.target || '' },
      ...(data['documentName'] ? [{ label: 'Document', value: data['documentName'] as string }] : [])
    ]);
  }

  private appendBatch(nodes: KnowledgeGraphNode[], isFirstBatch: boolean) {
    if (!this.cy) {
      this.initGraph();
      if (!this.cy) {
        setTimeout(() => this.appendBatch(nodes, isFirstBatch), 50);
        return;
      }
    }

    const newElements: cytoscape.ElementDefinition[] = [];
    const newlyAddedNodeIds: string[] = [];

    for (const node of nodes) {
      if (this.loadedNodeIds.has(node.id)) continue;
      this.loadedNodeIds.add(node.id);
      newlyAddedNodeIds.push(node.id);
      newElements.push({
        data: {
          id: node.id,
          name: truncateLabel(node.name),
          fullName: node.name,
          type: node.label,
          documentId: node.documentId ?? undefined,
          metadata: node.metadata ?? undefined
        }
      });
    }

    // Only walk edges touching the nodes THIS batch just added, via the
    // index built once when allEdges arrived — not a full rescan of every
    // fetched edge on every batch (that was O(batches × edges), the main
    // cost that compounded as a large graph streamed in).
    const candidateEdges = new Set<KnowledgeGraphEdge>();
    for (const nodeId of newlyAddedNodeIds) {
      for (const edge of this.edgesByNode.get(nodeId) ?? []) {
        candidateEdges.add(edge);
      }
    }

    for (const edge of candidateEdges) {
      const edgeKey = edge.id || `${edge.source}-${edge.target}`;
      if (this.addedEdgeIds.has(edgeKey)) continue;
      if (!this.loadedNodeIds.has(edge.source) || !this.loadedNodeIds.has(edge.target)) continue;

      this.addedEdgeIds.add(edgeKey);
      newElements.push({
        data: {
          id: edgeKey,
          source: edge.source,
          target: edge.target,
          type: edge.type,
          relationship: edge.relationship || edge.type,
          documentName: edge.documentName ?? undefined
        }
      });
    }

    this.nodeCount.set(this.loadedNodeIds.size);
    this.edgeCount.set(this.addedEdgeIds.size);

    if (newElements.length === 0) return;

    this.cy!.add(newElements);

    // Only the first batch gets an immediate layout here, for fast initial
    // visual feedback (it's at most NODE_BATCH_SIZE nodes, so cheap). Every
    // batch after that just adds elements — the one full relayout for
    // everything else added happens once, when fetchNextBatch's chain
    // finishes (see its `next` handler), not once per 300-node batch.
    if (isFirstBatch) {
      requestAnimationFrame(() => {
        this.runFcoseLayout(false);
        this.cy!.fit(undefined, 50);
      });
    }
  }

  private runFcoseLayout(incremental: boolean) {
    if (!this.cy) return;

    // fcose's default quality + 2500 iterations is fine for a small graph,
    // but is a single synchronous, unbreakable main-thread computation --
    // there's no web worker, no yielding between iterations. Cost scales
    // with edge count far more than node count, and a degree-sorted "most
    // connected" subgraph is dense by construction (observed live: 600
    // nodes, 15,169 edges, tab unresponsive on one layout pass). Scale both
    // down once the current graph crosses a size where that's a real risk,
    // rather than paying full-quality cost regardless of density.
    const nodeN = this.cy.nodes().length;
    const edgeN = this.cy.edges().length;
    const dense = edgeN > 1500 || nodeN > 800;

    this.cy.layout({
      name: 'fcose',
      quality: dense ? 'draft' : 'default',
      animate: false,
      fit: false,
      randomize: !incremental,
      nodeRepulsion: 4500,
      idealEdgeLength: 80,
      edgeElasticity: 0.45,
      numIter: dense ? 300 : 2500
    } as unknown as cytoscape.LayoutOptions).run();
  }
}
